import { classifyLink, sanitizeAnalyticsUrl } from './analytics-core.mjs'

const numberOrUndefined = (value) => {
  const number = Number(value)
  return Number.isFinite(number) && value !== '' && value !== undefined ? number : undefined
}

const compact = (value) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
  )

const targetSite = (url) => {
  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  if (host === 'rm.bonibeach.com') return 'boni_booking'
  if (host === 'bonibeach.com' || host.endsWith('.bonibeach.com')) return 'boni_hotel'
  return undefined
}

export function buildLinkInteractions({
  href = '',
  text = '',
  currentHost = 'selong-belanak.com',
  dataset = {},
  interactionType = 'click',
  handoffId,
} = {}) {
  let destination
  try {
    destination = new URL(href, `https://${currentHost}`)
  } catch {
    return []
  }

  const linkType = classifyLink({ href, text, currentHost })
  const sanitised = sanitizeAnalyticsUrl(destination.toString())
  const destinationDomain = destination.hostname.toLowerCase().replace(/^www\./, '')
  const safeDestinationUrl =
    linkType === 'phone'
      ? 'tel:'
      : linkType === 'email'
        ? 'mailto:'
        : linkType === 'whatsapp'
          ? `${destination.origin}/`
          : sanitised.url
  const destinationSiteId = targetSite(destination)
  const isBoniDestination =
    destinationSiteId === 'boni_hotel' || destinationSiteId === 'boni_booking'
  const businessId =
    dataset.analyticsBusinessId ||
    (isBoniDestination ? 'business:boni_beach_lombok' : undefined)
  const businessName =
    dataset.analyticsBusinessName || (isBoniDestination ? 'Boni Beach Lombok' : undefined)
  const businessCategory =
    dataset.analyticsBusinessCategory ||
    (destinationSiteId === 'boni_booking'
      ? 'Accommodation'
      : isBoniDestination
        ? 'Hotel & Restaurant'
        : undefined)
  const base = {
    destination_url: safeDestinationUrl,
    destination_domain: destinationDomain,
    interaction_type: interactionType,
  }

  // Only a link to a venue detail page selects a listing. Contact and website
  // links inside the same card keep their own event type and venue context.
  if (
    dataset.analyticsListingId &&
    linkType === 'internal' &&
    /^\/(where-to-stay|eat-drink|surf)\/[^/]+\/?$/.test(destination.pathname)
  ) {
    return [
      {
        eventName: 'listing_selected',
        properties: compact({
          listing_id: dataset.analyticsListingId,
          business_id: businessId,
          business_name: businessName,
          business_category: businessCategory,
          listing_position: numberOrUndefined(dataset.analyticsPosition),
          ...base,
        }),
      },
    ]
  }

  if (linkType === 'internal') {
    return [
      {
        eventName: 'navigation_selected',
        properties: compact({
          cta_id: dataset.analyticsCtaId,
          cta_label: text.trim(),
          placement_id: dataset.analyticsPlacementId,
          cta_type: 'internal_navigation',
          ...base,
        }),
      },
    ]
  }

  const isDirections =
    /(^|\.)google\.[a-z.]+$/i.test(destinationDomain) &&
    (/maps/i.test(destination.pathname) || /maps/i.test(destination.hostname))
  const isMenu = /\.pdf$/i.test(destination.pathname) || /\bmenu\b/i.test(text)
  const ctaType = isDirections
    ? 'directions'
    : isMenu
      ? 'menu'
      : linkType === 'owned_site_handoff'
        ? 'website'
        : linkType
  const ctaLabel = ['phone', 'email', 'whatsapp'].includes(ctaType)
    ? ctaType
    : text.trim()

  const eventName = ['phone', 'email', 'whatsapp'].includes(ctaType)
    ? 'contact_selected'
    : linkType === 'outbound' && !isDirections && !isMenu
      ? 'outbound_link_selected'
      : 'cta_selected'

  const ctaId = dataset.analyticsCtaId ||
    `${dataset.analyticsListingId || dataset.analyticsModuleId || 'link'}:${ctaType}:${safeDestinationUrl.split('?')[0]}`
  const listingContext = compact({
    listing_id: dataset.analyticsListingId,
    listing_position: numberOrUndefined(dataset.analyticsPosition),
    business_area: dataset.analyticsBusinessArea,
    business_section: dataset.analyticsBusinessSection,
    editors_pick: dataset.analyticsEditorsPick === undefined ? undefined : dataset.analyticsEditorsPick === 'true',
  })

  const events = [
    {
      eventName,
      properties: compact({
        cta_id: ctaId,
        cta_label: ctaLabel,
        placement_id: dataset.analyticsPlacementId,
        cta_type: ctaType,
        business_id: businessId,
        business_name: businessName,
        business_category: businessCategory,
        ...listingContext,
        ...base,
      }),
    },
  ]

  if (linkType === 'owned_site_handoff' || linkType === 'booking') {
    if (destinationSiteId) {
      events.push({
        eventName: 'site_handoff_started',
        properties: compact({
          handoff_id: handoffId,
          destination_site_id: destinationSiteId,
          handoff_source_site: 'selong_guide',
          handoff_placement_id: dataset.analyticsPlacementId,
          cta_id: ctaId,
          cta_type: ctaType,
          business_id: businessId,
          business_name: businessName,
          business_category: businessCategory,
          ...listingContext,
          ...base,
        }),
      })
    }
  }

  return events
}
